'use client'

import { useId, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import { z } from 'zod'

import { ApiError } from '@/lib/api/api-error'
import { FieldError, TextInput } from '@/components/ui/form-field'
import { subscribeNewsletterMutation } from './api/newsletter'

type NewsletterFieldValues = { email: string }

type NewsletterFormVariant = 'light' | 'dark'

interface NewsletterFormProps {
  translationNamespace: string
  variant?: NewsletterFormVariant
}

export function NewsletterForm({ translationNamespace, variant = 'light' }: NewsletterFormProps) {
  const t = useTranslations(translationNamespace)
  const errorId = `${useId()}-email-error`

  const schema = useMemo(
    () =>
      z.object({
        email: z.email({ message: t('newsletter.invalidEmail') }),
      }),
    [t],
  )

  const inputVariant = variant === 'dark' ? 'monoDark' : 'monoLight'

  const buttonClass =
    variant === 'dark'
      ? 'rounded-sm bg-brand-red px-8 py-4 text-xs font-bold uppercase tracking-widest text-white shadow-sm transition-colors hover:bg-brand-darkRed active:bg-brand-red disabled:opacity-60 disabled:cursor-not-allowed'
      : 'rounded-sm bg-charcoal-900 px-8 py-3 text-fs-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-brand-red active:bg-brand-darkRed disabled:opacity-60 disabled:cursor-not-allowed'

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
  } = useForm<NewsletterFieldValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
  })

  const mutation = useMutation(subscribeNewsletterMutation())

  const onSubmit = handleSubmit(async (data) => {
    try {
      await mutation.mutateAsync(data)
      toast.success(t('newsletter.successMessage'))
      reset()
    } catch (e) {
      if (ApiError.is(e) && e.status === 422) {
        const msg = e.fieldError('email')
        if (msg) {
          setError('email', { type: 'server', message: msg })
          return
        }
      }
      toast.error(t('newsletter.errorMessage'))
    }
  })

  return (
    <div className="mx-auto max-w-lg">
      <form className="flex flex-col gap-3 sm:flex-row" onSubmit={onSubmit} noValidate>
        <TextInput
          type="email"
          variant={inputVariant}
          placeholder={t('newsletter.placeholder')}
          aria-invalid={errors.email ? true : undefined}
          aria-describedby={errors.email ? errorId : undefined}
          className="flex-1"
          hasError={Boolean(errors.email)}
          disabled={mutation.isPending}
          {...register('email')}
        />
        <button type="submit" className={buttonClass} disabled={mutation.isPending}>
          {mutation.isPending ? t('newsletter.loading') : t('newsletter.btn')}
        </button>
      </form>
      <FieldError id={errorId} message={errors.email?.message} />
    </div>
  )
}
